import {
  CalendarOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  PrinterOutlined,
  ReloadOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import Text from 'antd/es/typography/Text';
import Title from 'antd/es/typography/Title';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import PropTypes from 'prop-types';
import {useMemo, useState} from 'react';
import {CgDanger} from 'react-icons/cg';
import {FaCheck, FaRupeeSign} from 'react-icons/fa';
import {FaSackDollar} from 'react-icons/fa6';
import {useSelector} from 'react-redux';

import {StatCard} from '../../../CommonComponents/Cards';
import {TableData} from '../../../CommonComponents/TableData';
import {
  RqAddSalaryAdvance,
  RqDeleteSalary,
  RqDeleteSalaryAdvance,
  RqGenerateSalaryAllByDateRange,
  RqGenerateSalaryByDateRange,
  RqGetAllSalaryAdvances,
  RqSupervisorLookupAll,
  RqUpdateSalaryAdvance,
} from '../../../reactQuery/apis';
import GenerateWagesPdf from '../../AdminScreens/SalaryManagementScreen/GenerateWagesPdf/GenerateWagesPdf';

import {GenerateSalaryModal} from './GenerateSalary';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const {RangePicker} = DatePicker;

const fmtINR = v => `₹ ${(v || 0).toLocaleString('en-IN')}`;

const getAttendanceCounts = (attendanceInfo = [], fromDate, toDate) => {
  const from = dayjs(fromDate);
  const to   = dayjs(toDate);
  const records = attendanceInfo.filter(a => {
    const d = dayjs(a.date);
    return d.isSameOrAfter(from, 'day') && d.isSameOrBefore(to, 'day');
  });
  return records.reduce(
    (acc, a) => {
      if (a.status === 'PRESENT')       {acc.present  += 1;}
      else if (a.status === 'ABSENT')   {acc.absent   += 1;}
      else if (a.status === 'HALF_DAY') {acc.halfDay  += 1;}
      acc.overtime += a.overtimeHours || 0;
      return acc;
    },
    {present: 0, absent: 0, halfDay: 0, overtime: 0},
  );
};

const getSalaryForRange = (salaryInfo = [], fromDate, toDate) => {
  if (!salaryInfo?.length) {return null;}
  const exact = salaryInfo.find(
    s => s.salaryFrom === fromDate && s.salaryTo === toDate,
  );
  if (exact) {return exact;}
  return salaryInfo.find(s => {
    const sFrom = dayjs(s.salaryFrom);
    const sTo   = dayjs(s.salaryTo);
    return sFrom.isSameOrBefore(dayjs(toDate), 'day') &&
           sTo.isSameOrAfter(dayjs(fromDate), 'day');
  }) || null;
};

// ─── Attendance Badge ─────────────────────────────────────────────────────────

const AttendanceBadge = ({present, absent, halfDay, overtime}) => (
  <div className={'flex flex-col gap-1 min-w-[80px]'}>
    <div className={'flex items-center gap-1.5'}>
      <span className={'w-2 h-2 rounded-full bg-green-500 shrink-0'} />
      <span className={'text-xs text-gray-700'}>
        <span className={'font-bold text-green-700'}>{present}</span> Present
      </span>
    </div>
    {halfDay > 0 && (
      <div className={'flex items-center gap-1.5'}>
        <span className={'w-2 h-2 rounded-full bg-yellow-400 shrink-0'} />
        <span className={'text-xs text-gray-700'}>
          <span className={'font-bold text-yellow-600'}>{halfDay}</span> Half Day
        </span>
      </div>
    )}
    {absent > 0 && (
      <div className={'flex items-center gap-1.5'}>
        <span className={'w-2 h-2 rounded-full bg-red-400 shrink-0'} />
        <span className={'text-xs text-gray-700'}>
          <span className={'font-bold text-red-600'}>{absent}</span> Absent
        </span>
      </div>
    )}
    {overtime > 0 && (
      <div className={'flex items-center gap-1.5'}>
        <span className={'w-2 h-2 rounded-full bg-blue-400 shrink-0'} />
        <span className={'text-xs text-gray-700'}>
          <span className={'font-bold text-blue-600'}>{overtime}h</span> OT
        </span>
      </div>
    )}
  </div>
);

AttendanceBadge.propTypes = {
  present: PropTypes.number, absent: PropTypes.number,
  halfDay: PropTypes.number, overtime: PropTypes.number,
};

const SalaryDetailModal = ({open, onClose, employee, token, deleteSalaryMutation, onDeleted}) => {
  if (!employee) {return null;}
  const salary = employee.salary;

  const handleDeleteSalary = () => {
    deleteSalaryMutation.mutate(
      {salaryId: salary.id, token},
      {
        onSuccess: () => {
          message.success('Salary record deleted');
          onClose();
          onDeleted?.();
        },
        onError: () => message.error('Failed to delete salary record'),
      },
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={[
        salary && (
          <Popconfirm
            key={'delete'}
            title={'Delete this salary record?'}
            description={'This action cannot be undone.'}
            onConfirm={handleDeleteSalary}
            okText={'Delete'}
            okButtonProps={{danger: true}}>
            <Button danger icon={<DeleteOutlined />} loading={deleteSalaryMutation?.isPending}>
              Delete
            </Button>
          </Popconfirm>
        ),
        <Button key={'close'} onClick={onClose}>Close</Button>,
      ]}
      title={(
        <div className={'flex items-center gap-2'}>
          <ThunderboltOutlined className={'text-yellow-500'} />
          <span className={'text-base font-semibold'}>Salary — {employee.name}</span>
          {salary
            ? <Tag color={'success'} icon={<CheckCircleOutlined />}>Generated</Tag>
            : <Tag color={'default'}>Not Generated</Tag>}
        </div>
      )}
      width={520}
      destroyOnClose>

      <div className={'mt-3 mb-4 flex flex-wrap items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm'}>
        <div>
          <p className={'text-gray-400 text-xs mb-0'}>Date range</p>
          <p className={'font-semibold text-gray-700 mb-0'}>
            {salary
              ? `${dayjs(salary.salaryFrom).format('DD MMM YYYY')} → ${dayjs(salary.salaryTo).format('DD MMM YYYY')}`
              : employee.rangeLabel}
          </p>
        </div>
        <div className={'border-l border-gray-300 pl-3'}>
          <p className={'text-gray-400 text-xs mb-0'}>Attendance</p>
          <p className={'font-semibold text-green-700 mb-0'}>
            {employee.attendancePresent}P / {employee.attendanceAbsent}A / {employee.attendanceHalfDay}H
          </p>
        </div>
        <div className={'border-l border-gray-300 pl-3'}>
          <p className={'text-gray-400 text-xs mb-0'}>Pending advance</p>
          <p className={`font-semibold mb-0 ${employee.totalAdvancePending > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {employee.totalAdvancePending > 0 ? fmtINR(employee.totalAdvancePending) : '—'}
          </p>
        </div>
      </div>

      {salary ? (
        <div className={'border border-blue-100 rounded-lg overflow-hidden'}>
          <div className={'bg-blue-50 px-4 py-2 border-b border-blue-100 flex items-center justify-between'}>
            <span className={'text-xs font-semibold uppercase tracking-wide text-blue-700'}>
              Salary Breakdown
            </span>
            {salary.distributedDate && (
              <span className={'text-xs text-gray-500'}>
                Distributed: {dayjs(salary.distributedDate).format('DD MMM YYYY')}
              </span>
            )}
          </div>
          <div className={'divide-y divide-gray-100'}>
            {[
              {label: 'Per Day Wage',          value: fmtINR(salary.perDayWage),          color: 'text-gray-800'},
              {label: 'Total Days',            value: salary.totalDays,                   color: 'text-gray-600'},
              {label: 'Present Days',          value: salary.presentDays,                 color: 'text-green-700 font-semibold'},
              {label: 'Absent Days',           value: salary.absentDays,                  color: salary.absentDays  > 0 ? 'text-red-600'    : 'text-gray-400'},
              {label: 'Half Days',             value: salary.halfDays,                    color: salary.halfDays    > 0 ? 'text-yellow-600' : 'text-gray-400'},
              {label: 'Payable Days',          value: salary.payableDays,                 color: 'text-blue-700 font-semibold'},
              {label: 'Overtime Amount',       value: fmtINR(salary.overtimeAmount),      color: salary.overtimeAmount > 0 ? 'text-blue-600' : 'text-gray-400'},
              {label: 'Gross Salary',          value: fmtINR(salary.grossSalary),         color: 'text-blue-700 font-bold text-base'},
              {label: 'Total Advance Pending', value: fmtINR(salary.totalAdvancePending), color: salary.totalAdvancePending > 0 ? 'text-red-600'    : 'text-gray-400'},
              {label: 'Advance Recovered',     value: fmtINR(salary.advanceRecovered),    color: salary.advanceRecovered    > 0 ? 'text-orange-600' : 'text-gray-400'},
            ].map(({label, value, color}) => (
              <div key={label} className={'flex items-center justify-between px-4 py-2.5'}>
                <span className={'text-xs text-gray-500'}>{label}</span>
                <span className={`text-sm ${color}`}>{value}</span>
              </div>
            ))}
          </div>
          <div className={'bg-green-50 border-t border-green-200 px-4 py-3 flex items-center justify-between'}>
            <span className={'text-sm font-semibold text-green-800'}>Net Salary Paid</span>
            <span className={'text-lg font-bold text-green-700'}>{fmtINR(salary.salaryPaid)}</span>
          </div>
        </div>
      ) : (
        <Alert
          type={'warning'}
          showIcon
          message={'No salary generated for this period'}
          description={
            'Use the "Generate All" button or click "Generate" on the action column ' +
            'to create a salary record for this employee.'
          }
        />
      )}
    </Modal>
  );
};

SalaryDetailModal.propTypes = {
  open: PropTypes.bool.isRequired, onClose: PropTypes.func.isRequired,
  employee: PropTypes.object,
  token: PropTypes.string,
  deleteSalaryMutation: PropTypes.object,
  onDeleted: PropTypes.func,
};

const GenerateAllModal = ({open, onClose, dateRange, token, generateAllMutation, totals}) => {
  const handleConfirm = () => {
    generateAllMutation.mutate(
      {fromDate: dateRange[0].format('YYYY-MM-DD'), toDate: dateRange[1].format('YYYY-MM-DD'), token},
      {
        onSuccess: () => { message.success('Salary generated for all employees'); onClose(); },
        onError:   () => message.error('Failed to generate salary for all employees'),
      },
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleConfirm}
      okText={'Generate All'}
      okButtonProps={{icon: <ThunderboltOutlined />}}
      title={(
        <div className={'flex items-center gap-2'}>
          <ThunderboltOutlined className={'text-yellow-500'} />
          <span className={'text-base font-semibold'}>Generate Salary — All Employees</span>
        </div>
      )}
      confirmLoading={generateAllMutation.isPending}
      destroyOnClose>
      <div className={'mt-3 p-4 bg-orange-50 border border-orange-200 rounded-lg text-sm space-y-3'}>
        <p className={'text-orange-700 font-medium'}>
          This will generate salary records for <strong>all employees</strong> for the
          selected date range.
        </p>
        <div className={'grid grid-cols-2 gap-3'}>
          <div>
            <p className={'text-gray-500 mb-0.5'}>Date Range</p>
            <p className={'font-semibold text-gray-800'}>
              {dateRange?.[0]?.format('DD MMM YYYY')} → {dateRange?.[1]?.format('DD MMM YYYY')}
            </p>
          </div>
          <div>
            <p className={'text-gray-500 mb-0.5'}>Total Employees</p>
            <p className={'font-bold text-gray-800'}>{totals?.employeeCount}</p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

GenerateAllModal.propTypes = {
  open: PropTypes.bool.isRequired, onClose: PropTypes.func.isRequired,
  dateRange: PropTypes.array, token: PropTypes.string,
  generateAllMutation: PropTypes.object.isRequired, totals: PropTypes.object,
};

const AdvanceModal = ({open, onClose, mode, initialValues, employees, token, addMutation, updateMutation}) => {
  const [form]    = Form.useForm();
  const isEdit    = mode === 'edit';
  const isLoading = addMutation.isPending || updateMutation.isPending;

  const handleSubmit = () => {
    form.validateFields().then(values => {
      const payload = {employeeId: values.employeeId, amount: values.amount, reason: values.reason};
      if (isEdit) {
        updateMutation.mutate(
          {id: initialValues.advanceId, payload, token},
          {
            onSuccess: () => { message.success('Advance updated'); form.resetFields(); onClose(); },
            onError:   () => message.error('Failed to update advance'),
          },
        );
      } else {
        addMutation.mutate(
          {payload, token},
          {
            onSuccess: () => { message.success('Advance added'); form.resetFields(); onClose(); },
            onError:   () => message.error('Failed to add advance'),
          },
        );
      }
    });
  };

  return (
    <Modal
      open={open}
      onCancel={() => { form.resetFields(); onClose(); }}
      onOk={handleSubmit}
      okText={isEdit ? 'Update Advance' : 'Add Advance'}
      title={<span className={'text-base font-semibold'}>{isEdit ? 'Edit Salary Advance' : 'Add Salary Advance'}</span>}
      confirmLoading={isLoading}
      destroyOnClose>
      <Form form={form} layout={'vertical'} initialValues={initialValues} className={'mt-4'}>
        <Form.Item name={'employeeId'} label={'Employee'} rules={[{required: true, message: 'Select an employee'}]}>
          <Select
            showSearch placeholder={'Select employee'} optionFilterProp={'label'} disabled={isEdit}
            options={employees.map(e => ({
              value: e.id,
              label: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
            }))}
          />
        </Form.Item>
        <Form.Item
          name={'amount'} label={'Advance Amount (₹)'}
          rules={[{required: true, message: 'Enter amount'}, {type: 'number', min: 0.01, message: 'Must be > 0'}]}>
          <InputNumber
            className={'w-full'} prefix={'₹'} min={0.01} step={0.01} placeholder={'e.g. 2000'}
            formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={v => v.replace(/,/g, '')}
          />
        </Form.Item>
        <Form.Item name={'reason'} label={'Reason'} rules={[{required: true, message: 'Enter reason'}]}>
          <Input.TextArea rows={3} placeholder={'e.g. Medical emergency'} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

AdvanceModal.propTypes = {
  open: PropTypes.bool.isRequired, onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired, initialValues: PropTypes.object,
  employees: PropTypes.array, token: PropTypes.string,
  addMutation: PropTypes.object.isRequired, updateMutation: PropTypes.object.isRequired,
};

const AdvanceDrawer = ({open, onClose, employee, token, deleteMutation, onEdit}) => {
  if (!employee) {return null;}
  const advances = employee.rawAdvances || [];

  const handleDelete = advanceId => {
    deleteMutation.mutate(
      {id: advanceId, token},
      {
        onSuccess: () => { message.success('Advance deleted'); onClose(); },
        onError:   () => message.error('Failed to delete advance'),
      },
    );
  };

  return (
    <Drawer
      open={open} onClose={onClose}
      title={<span className={'font-semibold'}>Advances — {employee.name}</span>}
      width={420}>
      {advances.length === 0 ? (
        <Empty description={'No advances recorded'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <div className={'flex flex-col gap-3'}>
          {advances.map(adv => (
            <div key={adv.id} className={'border border-gray-200 rounded-lg p-3 bg-gray-50'}>
              <div className={'flex items-start justify-between gap-2'}>
                <div className={'flex flex-col gap-1'}>
                  <span className={'text-base font-bold text-red-600'}>
                    - ₹ {(adv.amount || 0).toLocaleString('en-IN')}
                  </span>
                  {adv.recoveredAmount > 0 && (
                    <span className={'text-xs text-orange-600'}>
                      Recovered: ₹ {(adv.recoveredAmount || 0).toLocaleString('en-IN')}
                    </span>
                  )}
                  {adv.remainingAdvance > 0 && (
                    <span className={'text-xs text-red-500 font-medium'}>
                      Remaining: ₹ {(adv.remainingAdvance || 0).toLocaleString('en-IN')}
                    </span>
                  )}
                  <span className={'text-xs text-gray-500'}>
                    {adv.advanceDate ? dayjs(adv.advanceDate).format('DD MMM YYYY') : '—'}
                  </span>
                  {adv.reason      && <span className={'text-sm text-gray-700 mt-1'}>{adv.reason}</span>}
                  {adv.markedByRole && <Tag color={'blue'} className={'w-fit text-xs mt-1'}>{adv.markedByRole}</Tag>}
                </div>
                <div className={'flex gap-2 shrink-0'}>
                  <Tooltip title={'Edit'}>
                    <Button
                      size={'small'} icon={<EditOutlined />}
                      onClick={() => onEdit({
                        advanceId: adv.id, employeeId: adv.employeeId,
                        amount: adv.amount, reason: adv.reason,
                      })}
                    />
                  </Tooltip>
                  <Popconfirm
                    title={'Delete this advance?'} description={'This action cannot be undone.'}
                    onConfirm={() => handleDelete(adv.id)} okText={'Delete'} okButtonProps={{danger: true}}>
                    <Button size={'small'} danger icon={<DeleteOutlined />} loading={deleteMutation.isPending} />
                  </Popconfirm>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
};

AdvanceDrawer.propTypes = {
  open: PropTypes.bool.isRequired, onClose: PropTypes.func.isRequired,
  employee: PropTypes.object, token: PropTypes.string,
  deleteMutation: PropTypes.object.isRequired, onEdit: PropTypes.func.isRequired,
};

const SalaryManagementScreen = () => {
  const token = useSelector(state => state.auth.tokenData);

  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  // Advance CRUD modal
  const [modalOpen,          setModalOpen]          = useState(false);
  const [modalMode,          setModalMode]          = useState('add');
  const [modalInitialValues, setModalInitialValues] = useState({});

  // Advance drawer
  const [drawerEmployee, setDrawerEmployee] = useState(null);
  const [drawerOpen,     setDrawerOpen]     = useState(false);

  // PDF
  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  // View salary modal (read-only, salary already generated)
  const [viewEmployee,  setViewEmployee]  = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // Generate salary modal (only when NOT yet generated)
  const [genEmployee,  setGenEmployee]  = useState(null);
  const [genModalOpen, setGenModalOpen] = useState(false);

  // Generate-all modal
  const [genAllOpen, setGenAllOpen] = useState(false);

  const {data: employees = [], isLoading: empLoading, refetch: refetchEmployees} =
    RqSupervisorLookupAll(token, {enabled: !!token});

  const {isLoading: advLoading} = RqGetAllSalaryAdvances(token, {enabled: !!token});

  const addMutation         = RqAddSalaryAdvance();
  const updateMutation      = RqUpdateSalaryAdvance();
  const deleteMutation      = RqDeleteSalaryAdvance();
  const generateMutation    = RqGenerateSalaryByDateRange();
  const generateAllMutation = RqGenerateSalaryAllByDateRange();
  const deleteSalaryMutation = RqDeleteSalary();

  const isLoading = empLoading || advLoading;

  const fromDate = dateRange?.[0]?.format('YYYY-MM-DD');
  const toDate   = dateRange?.[1]?.format('YYYY-MM-DD');

  const rangeLabel = fromDate && toDate
    ? `${dateRange[0].format('DD MMM YYYY')} – ${dateRange[1].format('DD MMM YYYY')}`
    : '—';

  const tableData = useMemo(() => {
    if (!fromDate || !toDate) {return [];}

    return employees.map((emp, idx) => {
      const fullName   = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
      const attendance = getAttendanceCounts(emp.attendanceInfo, fromDate, toDate);
      const salary     = getSalaryForRange(emp.salaryInfo, fromDate, toDate);

      const perDayWages    = emp.perDaysWages || 0;
      const wagesRate      = emp.wagesRate    || 0;
      const totalWagesRate = wagesRate > 0 ? wagesRate : perDayWages * 26;

      const advanceList   = emp.salaryAdvanceInfo || [];
      const latestAdvance = [...advanceList].sort(
        (a, b) => new Date(b.advanceDate) - new Date(a.advanceDate),
      )[0];
      const totalAdvance = latestAdvance
        ? Math.max((latestAdvance.amount || 0) - (latestAdvance.recoveredAmount || 0), latestAdvance.remainingAdvance || 0)
        : 0;

      const estimatedPresentDays = attendance.present + attendance.halfDay * 0.5;

      const overtimeAmount = salary?.overtimeAmount || 0;

      const estimatedGross = (estimatedPresentDays * perDayWages) + overtimeAmount;

      return {
        key:                emp.id,
        srNo:               idx + 1,
        name:               fullName || '—',
        employeeId:         emp.id,
        rangeLabel,

        // attendance badge
        attendancePresent:  attendance.present,
        attendanceAbsent:   attendance.absent,
        attendanceHalfDay:  attendance.halfDay,
        attendanceOvertime: attendance.overtime,

        // employee base data
        perDayWages,
        totalWagesRate,
        status:      emp.status,
        rawAdvances: advanceList,

        // overtime
        overtimeAmount,

        // advance figures:
        // - when generated → pull from salary API (accurate per-period values)
        // - when NOT generated → use remainingAdvance (rolling balance)
        advanceDeducted:     totalAdvance,                                              // for display in not-generated state
        totalAdvancePending: salary ? salary.totalAdvancePending : totalAdvance,       // pending balance
        advanceRecovered:    salary ? salary.advanceRecovered    : 0,                  // recovered this period

        // salary figures
        salary,
        presentDays:  salary ? salary.presentDays  : estimatedPresentDays,
        absentDays:   salary ? salary.absentDays   : attendance.absent,
        halfDays:     salary ? salary.halfDays     : attendance.halfDay,
        payableDays:  salary ? salary.payableDays  : estimatedPresentDays,
        grossSalary:  salary ? salary.grossSalary  : estimatedGross,
        salaryPaid:   salary ? salary.salaryPaid   : null,
        isGenerated:  !!salary,

        wages: estimatedGross,
      };
    });
  }, [employees, fromDate, toDate, rangeLabel]);

  const totals = useMemo(() =>
    tableData.reduce(
      (acc, r) => {
        acc.employeeCount     += 1;
        acc.attendancePresent += r.attendancePresent;
        acc.presentDays       += r.presentDays;
        acc.grossSalary       += r.isGenerated ? (r.grossSalary || 0)      : 0;
        acc.advanceRecovered  += r.isGenerated ? (r.advanceRecovered || 0) : 0;
        acc.salaryPaid        += r.isGenerated ? (r.salaryPaid || 0)       : 0;
        acc.generatedCount    += r.isGenerated ? 1 : 0;
        return acc;
      },
      {employeeCount: 0, attendancePresent: 0, presentDays: 0,
        grossSalary: 0, advanceRecovered: 0, salaryPaid: 0, generatedCount: 0},
    ),
  [tableData]);

  const openAddModal  = (employeeId = undefined) => {
    setModalMode('add');
    setModalInitialValues(employeeId ? {employeeId} : {});
    setModalOpen(true);
  };
  const openEditModal = values => {
    setModalMode('edit');
    setModalInitialValues(values);
    setDrawerOpen(false);
    setModalOpen(true);
  };
  const openDrawer = row => { setDrawerEmployee(row); setDrawerOpen(true); };

  const openSalaryModal = row => {
    if (row.isGenerated) {
      setViewEmployee(row);
      setViewModalOpen(true);
    } else {
      setGenEmployee(row);
      setGenModalOpen(true);
    }
  };

  const columns = [
    {
      title: 'SR.', dataIndex: 'srNo', key: 'srNo',
      width: 55, align: 'center', className: 'font-semibold',
    },
    {
      title: 'NAME OF WORKER', dataIndex: 'name', key: 'name', width: 175,
      render: (name, record) => (
        <div className={'flex flex-col gap-0.5'}>
          <span className={'font-semibold text-gray-800 text-sm'}>{name}</span>
          <Tag color={record.status === 'ACTIVE' ? 'green' : 'default'} className={'w-fit text-xs px-1 py-0'}>
            {record.status}
          </Tag>
        </div>
      ),
    },
    {
      title: 'ATTENDANCE', key: 'attendance', width: 145,
      render: (_, r) => (
        <AttendanceBadge
          present={r.attendancePresent} absent={r.attendanceAbsent}
          halfDay={r.attendanceHalfDay} overtime={r.attendanceOvertime}
        />
      ),
    },
    {
      title: 'PRESENT / PAYABLE', key: 'presentDays', align: 'center', width: 130,
      render: (_, r) => (
        <div className={'flex flex-col items-center gap-0.5'}>
          <span className={'inline-flex items-center justify-center w-10 h-8 rounded-full bg-green-100 text-green-700 font-bold text-sm'}>
            {Number.isInteger(r.presentDays) ? r.presentDays : r.presentDays?.toFixed(1)}
          </span>
          {r.isGenerated && r.payableDays !== r.presentDays && (
            <span className={'text-xs text-blue-600 font-medium'}>{r.payableDays} payable</span>
          )}
        </div>
      ),
    },
    {
      title: 'PER DAY WAGES', dataIndex: 'perDayWages', key: 'perDayWages',
      align: 'right', width: 120,
      render: v => <span className={'text-gray-600'}>{fmtINR(v)}</span>,
    },
    {
      title: 'WAGES RATE (26d)', dataIndex: 'totalWagesRate', key: 'totalWagesRate',
      align: 'right', width: 130,
      render: v => <span className={'text-gray-600'}>{fmtINR(v)}</span>,
    },
    {
      title: 'OVERTIME', key: 'overtimeAmount', align: 'right', width: 120,
      render: (_, r) => r.isGenerated ? (
        r.overtimeAmount > 0 ? (
          <span className={'font-medium text-blue-600'}>{fmtINR(r.overtimeAmount)}</span>
        ) : (
          <span className={'text-gray-400 text-xs'}>—</span>
        )
      ) : (
        <span className={'text-gray-400 text-xs'}>—</span>
      ),
    },
    {
      title: 'GROSS SALARY', key: 'grossSalary', align: 'right', width: 130,
      render: (_, r) => r.isGenerated ? (
        <span className={'font-semibold text-blue-700'}>{fmtINR(r.grossSalary)}</span>
      ) : (
        <Tooltip title={'Not yet generated — showing estimate'}>
          <span className={'text-blue-400 border-b border-dashed border-blue-300 cursor-default text-xs'}>
            ~{fmtINR(r.wages)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '(-) ADVANCE', key: 'advance', align: 'right', width: 205,
      render: (_, record) => (
        <div className={'flex items-center justify-end gap-2'}>
          <div className={'flex flex-col items-end'}>
            {record.isGenerated ? (
              <>
                {record.advanceRecovered > 0 && (
                  <span className={'text-orange-600 font-medium text-xs'}>
                    - {fmtINR(record.advanceRecovered)} recovered
                  </span>
                )}
                {record.totalAdvancePending > 0 && (
                  <span className={'text-red-500 text-xs'}>
                    {fmtINR(record.totalAdvancePending)} pending
                  </span>
                )}
                {record.advanceRecovered === 0 && record.totalAdvancePending === 0 && (
                  <span className={'text-gray-400 text-xs'}>—</span>
                )}
              </>
            ) : (
              <>
                {record.totalAdvancePending > 0 ? (
                  <span className={'text-red-600 font-medium text-sm'}>
                    {fmtINR(record.totalAdvancePending)} pending
                  </span>
                ) : (
                  <span className={'text-gray-400 text-sm'}>—</span>
                )}
              </>
            )}
          </div>
          <div className={'flex gap-1'}>
            {record.rawAdvances.length > 0 && (
              <Tooltip title={'View advances'}>
                <Button size={'small'} icon={<EyeOutlined />} onClick={() => openDrawer(record)} />
              </Tooltip>
            )}
            <Tooltip title={'Add advance'}>
              <Button size={'small'} icon={<PlusOutlined />} onClick={() => openAddModal(record.employeeId)} />
            </Tooltip>
          </div>
        </div>
      ),
    },
    {
      title: 'NET SALARY PAID', key: 'salaryPaid', align: 'right', width: 140,
      render: (_, r) => r.isGenerated ? (
        <span className={'font-bold text-green-700 text-base'}>{fmtINR(r.salaryPaid)}</span>
      ) : (
        <Tag color={'default'} className={'text-xs'}>Pending</Tag>
      ),
    },
    {
      title: 'STATUS', key: 'status', align: 'center', width: 110,
      render: (_, r) => r.isGenerated ? (
        <Tag color={'success'} icon={<CheckCircleOutlined />} className={'text-xs'}>Generated</Tag>
      ) : (
        <Tag color={'warning'} className={'text-xs'}>Not Generated</Tag>
      ),
    },
    {
      title: 'ACTION', key: 'action', align: 'center', width: 155, fixed: 'right',
      render: (_, record) => {
        const hasAdv        = record.totalAdvancePending > 0;
        const isThisLoading = generateMutation.isPending && genEmployee?.employeeId === record.employeeId;

        return record.isGenerated ? (
          
          <Tooltip title={'View salary details'}>
            <Button
              size={'small'}
              icon={<EyeOutlined />}
              onClick={() => openSalaryModal(record)}>
              View Details
            </Button>
          </Tooltip>
        ) : (
          
          <Tooltip title={hasAdv ? 'Generate salary — advance pending' : 'Calculate & generate salary'}>
            <Button
              size={'small'}
              type={'primary'}
              icon={<PlayCircleOutlined />}
              loading={isThisLoading}
              style={hasAdv
                ? {background: '#f97316', borderColor: '#ea580c'}
                : {background: '#1d4ed8', borderColor: '#1d4ed8'}}
              onClick={() => openSalaryModal(record)}>
              {hasAdv ? 'Calculate + Adv' : 'Calculate Salary'}
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div className={'min-h-screen bg-gray-50 md:p-6 p-1'}>

      <div className={'flex items-center justify-between mb-6 flex-wrap gap-3'}>
        <div className={'flex items-center gap-3'}>
          <div
            className={'w-12 h-10 rounded-xl flex items-center justify-center text-white'}
            style={{background: 'linear-gradient(135deg, #E83F25, #A62C2C)'}}>
            <FaRupeeSign style={{fontSize: 22}} />
          </div>
          <Title level={3} className={'header !mb-0'}>Salary Management</Title>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} className={'btn-secondary'} onClick={refetchEmployees} loading={isLoading}>
            Refresh
          </Button>
          <Button icon={<PlusOutlined />} className={'btn-primary'} onClick={() => openAddModal()}>
            Add Advance
          </Button>
          
        </Space>
      </div>

      <div className={'bg-white rounded-lg shadow-sm border border-primary-100 mb-5 p-5'}>
        <div className={'flex flex-wrap items-center justify-between gap-3'}>
          <div className={'flex items-center gap-3'}>
            <CalendarOutlined className={'text-blue-500 text-lg'} />
            <div className={'px-4 py-1.5 bg-blue-50 border border-blue-200 rounded-lg'}>
              <Text className={'text-blue-700 font-bold text-base tracking-wide uppercase'}>
                {rangeLabel}
              </Text>
            </div>
            <Text className={'text-gray-400 text-xs uppercase tracking-wide hidden sm:block'}>
              Wages Statement
            </Text>
          </div>
          <div className={'flex flex-wrap items-center gap-2'}>
            <RangePicker
              value={dateRange} onChange={val => val && setDateRange(val)}
              format={'DD MMM YYYY'} allowClear={false} style={{minWidth: 260}}
            />
            <Button
              icon={<PrinterOutlined />} ghost className={'btn-primary'}
              disabled={tableData.length === 0} onClick={() => setPdfModalOpen(true)}>
              Print
            </Button>
          </div>
        </div>
      </div>

      {!isLoading && tableData.length > 0 && (
        <div className={'grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5'}>
          <StatCard
            label={'Total Employees'}
            value={`${totals.generatedCount} / ${totals.employeeCount}`}
            icon={<TeamOutlined className={'bg-color'} />} bg={'#e6e9ec'}
          />
          <StatCard
            label={'Total Present'}
            value={totals.attendancePresent}
            icon={<FaCheck className={'bg-color'} />} bg={'#e6e9ec'}
          />
          <StatCard
            label={'Gross Salary'}
            value={`₹ ${(totals.grossSalary || 0).toLocaleString('en-IN')}`}
            icon={<CgDanger className={'bg-color'} />} bg={'#e6e9ec'}
          />
          <StatCard
            label={'Advance Recovered'}
            value={`₹ ${(totals.advanceRecovered || 0).toLocaleString('en-IN')}`}
            icon={<FaSackDollar className={'bg-color'} />} bg={'#e6e9ec'}
          />
          <StatCard
            label={'Net Salary Paid'}
            value={`₹ ${(totals.salaryPaid || 0).toLocaleString('en-IN')}`}
            icon={<FaRupeeSign className={'bg-color'} />} bg={'#e6e9ec'}
          />
        </div>
      )}

      <div className={'bg-white rounded-md shadow-sm border border-primary-100 overflow-hidden'}>
        <Spin spinning={isLoading} description={'Loading wage data...'}>
          <TableData
            columns={columns}
            dataSource={tableData}
            scroll={{x: 1500}}
            locale={{
              emptyText: (
                <Empty
                  description={'No employee data found for this period'}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
            summary={() =>
              tableData.length > 0 ? (
                <Table.Summary fixed>
                  <Table.Summary.Row className={'bg-gray-50'}>
                    <Table.Summary.Cell index={0} colSpan={2} align={'center'}>
                      <span className={'font-bold text-gray-800 uppercase tracking-wide text-xs'}>TOTAL</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <div className={'flex items-center gap-1.5'}>
                        <span className={'w-2 h-2 rounded-full bg-green-500'} />
                        <span className={'text-xs font-bold text-green-700'}>{totals.attendancePresent} Present</span>
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align={'center'}>
                      <span className={'font-bold text-gray-800'}>
                        {Number.isInteger(totals.presentDays) ? totals.presentDays : totals.presentDays.toFixed(1)}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} />
                    <Table.Summary.Cell index={5} />
                    <Table.Summary.Cell index={6} />
                    <Table.Summary.Cell index={7} align={'right'}>
                      <span className={'font-bold text-blue-700'}>
                        ₹ {(totals.grossSalary || 0).toLocaleString('en-IN')}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={8} align={'right'}>
                      <span className={'font-bold text-orange-600'}>
                        {totals.advanceRecovered > 0 ? `- ₹ ${(totals.advanceRecovered || 0).toLocaleString('en-IN')}` : '—'}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={9} align={'right'}>
                      <span className={'font-bold text-green-700 text-base'}>
                        ₹ {(totals.salaryPaid || 0).toLocaleString('en-IN')}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={10} />
                    <Table.Summary.Cell index={11} />
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
        </Spin>
      </div>

      <AdvanceModal
        open={modalOpen} onClose={() => setModalOpen(false)}
        mode={modalMode} initialValues={modalInitialValues}
        employees={employees} token={token}
        addMutation={addMutation} updateMutation={updateMutation}
      />
      <AdvanceDrawer
        open={drawerOpen} onClose={() => setDrawerOpen(false)}
        employee={drawerEmployee} token={token}
        deleteMutation={deleteMutation} onEdit={openEditModal}
      />

      <SalaryDetailModal
        open={viewModalOpen}
        onClose={() => { setViewModalOpen(false); setViewEmployee(null); }}
        employee={viewEmployee}
        token={token}
        deleteSalaryMutation={deleteSalaryMutation}
        onDeleted={refetchEmployees}
      />

      <GenerateSalaryModal
        open={genModalOpen}
        onClose={() => { setGenModalOpen(false); setGenEmployee(null); }}
        employee={genEmployee}
        defaultDateRange={dateRange}
        token={token}
        generateMutation={generateMutation}
      />

      <GenerateAllModal
        open={genAllOpen} onClose={() => setGenAllOpen(false)}
        dateRange={dateRange} token={token}
        generateAllMutation={generateAllMutation} totals={totals}
      />

      <GenerateWagesPdf
        open={pdfModalOpen} onClose={() => setPdfModalOpen(false)}
        tableData={tableData} totals={totals}
        monthLabel={rangeLabel} selectedYear={dateRange?.[1]?.year()}
      />
    </div>
  );
};

export default SalaryManagementScreen;
